import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { templateApi, runApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './TemplateReview.css';

function TemplateReview() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [testMode, setTestMode] = useState(false);
    const [testInputs, setTestInputs] = useState({});
    const [testResult, setTestResult] = useState(null);
    const [testRunning, setTestRunning] = useState(false);
    const [activating, setActivating] = useState(false);

    useEffect(() => {
        loadTemplate();
    }, [id]);

    const loadTemplate = async () => {
        try {
            const data = await templateApi.get(id);

            // Ensure inputs_definition and logic_definition are arrays
            if (data.inputs_definition) {
                if (typeof data.inputs_definition === 'string') {
                    data.inputs_definition = JSON.parse(data.inputs_definition);
                }
            }
            if (data.logic_definition) {
                if (typeof data.logic_definition === 'string') {
                    data.logic_definition = JSON.parse(data.logic_definition);
                }
            }

            setTemplate(data);

            // Initialize test inputs with empty values
            if (data.inputs_definition && Array.isArray(data.inputs_definition)) {
                const inputs = {};
                data.inputs_definition.forEach(input => {
                    if (input && typeof input === 'object' && input.name) {
                        inputs[input.name] = '';
                    }
                });
                setTestInputs(inputs);
            }
        } catch (err) {
            setError(err.message || 'Failed to load template');
        } finally {
            setLoading(false);
        }
    };

    const handleTestRun = async () => {
        setTestRunning(true);
        setTestResult(null);
        setError(null);

        try {
            // Convert test inputs to the format expected by the API
            const inputData = {};
            Object.keys(testInputs).forEach(key => {
                const value = testInputs[key];
                // Try to parse as number if it looks like one
                inputData[key] = isNaN(value) || value === '' ? value : parseFloat(value);
            });

            const result = await runApi.execute(id, inputData);
            setTestResult(result);
        } catch (err) {
            setError(err.message || 'Test execution failed');
        } finally {
            setTestRunning(false);
        }
    };

    const handleActivate = async () => {
        if (!window.confirm('Activate this template? It will become available to your team.')) {
            return;
        }

        setActivating(true);
        setError(null);

        try {
            await templateApi.activate(id);
            navigate('/app/governance', {
                state: { message: 'Template activated successfully!' }
            });
        } catch (err) {
            setError(err.message || 'Activation failed');
            setActivating(false);
        }
    };

    if (loading) {
        return <LoadingSpinner message="Loading template..." />;
    }

    if (error && !template) {
        return (
            <div className="template-review">
                <div className="alert alert-error">{error}</div>
                <button className="btn btn-secondary" onClick={() => navigate('/app/governance')}>
                    Back to Governance
                </button>
            </div>
        );
    }

    const logicRules = Array.isArray(template.logic_definition) ? template.logic_definition : [];
    const inputs = Array.isArray(template.inputs_definition) ? template.inputs_definition : [];

    return (
        <div className="template-review fade-in">
            <div className="review-header">
                <div>
                    <h1>Review Template</h1>
                    <p className="text-muted">Verify the extracted logic before activating</p>
                </div>
                <div className="review-header-actions">
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate('/app/governance')}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-success btn-lg"
                        onClick={handleActivate}
                        disabled={activating || template.is_active}
                    >
                        {activating ? 'Activating...' : template.is_active ? 'Already Active' : 'Activate Template'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-error mb-4">{error}</div>
            )}

            {template.is_active && (
                <div className="alert alert-success mb-4">
                    ✅ This template is already active and available to your team.
                </div>
            )}

            {/* Template Info */}
            <div className="review-section">
                <div className="review-section-header">
                    <h2>Template Information</h2>
                </div>
                <div className="review-card">
                    <div className="review-info-grid">
                        <div className="review-info-item">
                            <label>Name</label>
                            <span>{template.name}</span>
                        </div>
                        <div className="review-info-item">
                            <label>Version</label>
                            <span>v{template.version}</span>
                        </div>
                        <div className="review-info-item">
                            <label>Status</label>
                            <span className={`badge ${template.is_active ? 'badge-success' : 'badge-warning'}`}>
                                {template.is_active ? 'Active' : 'Draft'}
                            </span>
                        </div>
                        <div className="review-info-item">
                            <label>Rules Extracted</label>
                            <span>{logicRules.length} calculation rules</span>
                        </div>
                    </div>
                    {template.description && (
                        <div className="review-description">
                            <label>Description</label>
                            <p>{template.description}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Structural Integrity Warnings */}
            {(() => {
                const metadata = typeof template.metadata === 'string' ? JSON.parse(template.metadata) : template.metadata;
                const warnings = metadata?.structuralScan?.totalWarnings || 0;

                if (warnings > 0) {
                    return (
                        <div className="review-section">
                            <div className="review-section-header">
                                <h2 className="text-error">Structural Integrity Warnings</h2>
                                <span className="badge badge-error">{warnings} Issues Found</span>
                            </div>
                            <div className="review-card alert-card-error">
                                <div className="integrity-warning-content">
                                    <div className="integrity-warning-header">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-error">
                                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                            <line x1="12" y1="9" x2="12" y2="13" />
                                            <line x1="12" y1="17" x2="12.01" y2="17" />
                                        </svg>
                                        <h3>Potentially Problematic Rows Detected</h3>
                                    </div>
                                    <p>Our audit of the spreadsheet structure found inconsistencies. This usually happens when someone manually types a value over a formula or uses a non-standard calculation for specific rows.</p>
                                    <ul className="integrity-warning-list">
                                        <li><strong>Manual Overrides:</strong> Cells that should likely have formulas but contain hardcoded numbers.</li>
                                        <li><strong>Formula Deviations:</strong> Rows that use a different calculation logic than the rest of the column.</li>
                                    </ul>
                                    <div className="integrity-recommendation">
                                        <strong>Recommendation:</strong> Verify these rows in the original file to ensure the extracted rules represent your intended logic.
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="review-section">
                        <div className="review-card alert-card-success">
                            <div className="integrity-success">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-success">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <span><strong>Structural Integrity Passed:</strong> No manual overrides or inconsistent formulas detected in logical columns.</span>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Inputs */}
            <div className="review-section">
                <div className="review-section-header">
                    <h2>Required Inputs</h2>
                    <span className="review-section-badge">{inputs.length} inputs</span>
                </div>
                <div className="review-card">
                    {inputs.length === 0 ? (
                        <div className="review-empty">
                            <p>No inputs required - this template uses only static calculations.</p>
                        </div>
                    ) : (
                        <div className="review-inputs-list">
                            {inputs.map((input, idx) => {
                                // Ensure input is an object and has a name
                                if (!input || typeof input !== 'object') return null;

                                const inputName = typeof input.name === 'string' ? input.name :
                                    typeof input.cell === 'string' ? input.cell :
                                        `Input ${idx + 1}`;
                                const inputType = typeof input.type === 'string' ? input.type : 'any';

                                return (
                                    <div key={idx} className="review-input-item">
                                        <div className="review-input-icon">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                <circle cx="12" cy="7" r="4" />
                                            </svg>
                                        </div>
                                        <div className="review-input-details">
                                            <span className="review-input-name">{inputName}</span>
                                            <span className="review-input-type">{inputType}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Calculation Rules */}
            <div className="review-section">
                <div className="review-section-header">
                    <h2>Calculation Logic</h2>
                    <span className="review-section-badge">{logicRules.length} rules</span>
                </div>
                <div className="review-card">
                    {logicRules.length === 0 ? (
                        <div className="review-empty">
                            <p>No calculation rules found in the spreadsheet.</p>
                        </div>
                    ) : (
                        <div className="review-rules-list">
                            {logicRules.slice(0, 10).map((rule, idx) => {
                                // Ensure rule is an object
                                if (!rule || typeof rule !== 'object') return null;

                                const output = typeof rule.output === 'string' ? rule.output : `Output ${idx + 1}`;
                                const formula = typeof rule.formula === 'string' ? rule.formula :
                                    typeof rule.expression === 'string' ? rule.expression :
                                        'Static value';
                                const deps = Array.isArray(rule.dependencies) ? rule.dependencies : [];

                                return (
                                    <div key={idx} className="review-rule-item">
                                        <div className="review-rule-number">{idx + 1}</div>
                                        <div className="review-rule-content">
                                            <div className="review-rule-output">
                                                <strong>{output}</strong>
                                            </div>
                                            <div className="review-rule-formula">
                                                {formula}
                                            </div>
                                            {deps.length > 0 && (
                                                <div className="review-rule-deps">
                                                    Depends on: {deps.join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {logicRules.length > 10 && (
                                <div className="review-rules-more">
                                    + {logicRules.length - 10} more rules
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Test Execution */}
            <div className="review-section">
                <div className="review-section-header">
                    <h2>Test Execution</h2>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setTestMode(!testMode)}
                    >
                        {testMode ? 'Hide Test' : 'Test Template'}
                    </button>
                </div>

                {testMode && (
                    <div className="review-card">
                        <div className="review-test-section">
                            <p className="review-test-description">
                                Enter sample values to test the template before activating it.
                            </p>

                            {inputs.length === 0 ? (
                                <div className="alert alert-info">
                                    This template doesn't require inputs. Click "Run Test" to see the output.
                                </div>
                            ) : (
                                <div className="review-test-inputs">
                                    {inputs.map((input, idx) => (
                                        <div key={idx} className="form-group">
                                            <label className="form-label">{input.name}</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={testInputs[input.name] || ''}
                                                onChange={(e) => setTestInputs({
                                                    ...testInputs,
                                                    [input.name]: e.target.value
                                                })}
                                                placeholder={`Enter ${input.name}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button
                                className="btn btn-primary"
                                onClick={handleTestRun}
                                disabled={testRunning}
                            >
                                {testRunning ? 'Running...' : 'Run Test'}
                            </button>

                            {testResult && (
                                <div className="review-test-result">
                                    <h4>Test Results</h4>
                                    <div className="review-test-output">
                                        <pre>{JSON.stringify(testResult.outputs || testResult, null, 2)}</pre>
                                    </div>
                                    <div className="review-test-meta">
                                        <span>✅ Execution successful</span>
                                        {testResult.duration_ms && (
                                            <span>⚡ {testResult.duration_ms}ms</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TemplateReview;
